import { useApi } from '@classgo/app-kit';
import { createPeriodInputSchema, updatePeriodInputSchema } from '@classgo/shared';
import { Input } from '@classgo/ui';

import { academicApi } from '../academic/api';
import type { PeriodView } from '../academic/types';
import { useT } from '../i18n';
import { useForm } from '../lib/form';
import { FormShell } from './form-shell';

/**
 * Create or edit a period of a school year. The API refuses periods outside the year or
 * overlapping a sibling; those messages land next to the start date.
 */
export const PeriodForm = ({
  schoolYearId,
  nextPosition,
  current,
  onDone,
}: {
  schoolYearId: string;
  nextPosition: number;
  current: PeriodView | null;
  onDone: () => void;
}) => {
  const t = useT();
  const api = useApi();
  const form = useForm({
    schema: current ? updatePeriodInputSchema : createPeriodInputSchema,
    initial: {
      name: current?.name ?? t('periods.defaultName', { n: nextPosition }),
      position: String(current?.position ?? nextPosition),
      startsOn: current?.startsOn ?? '',
      endsOn: current?.endsOn ?? '',
    },
    toInput: (values) => ({
      ...(current ? {} : { schoolYearId }),
      name: values.name,
      position: values.position.trim() === '' ? undefined : Number(values.position),
      startsOn: values.startsOn,
      endsOn: values.endsOn,
    }),
  });
  const error = (key: 'name' | 'position' | 'startsOn' | 'endsOn') =>
    form.fieldError(key) ? { error: form.fieldError(key) } : {};
  return (
    <FormShell
      error={form.errors.form}
      busy={form.busy}
      onSubmit={(event) => {
        event.preventDefault();
        void form
          .submit(async (input) => {
            if (current) await academicApi(api).update('periods', current.id, input);
            else await academicApi(api).create('periods', input);
          })
          .then((ok) => ok && onDone());
      }}
    >
      <div className="grid gap-4 sm:grid-cols-[1fr_6rem]">
        <Input
          label={t('periods.field.name')}
          required
          value={form.values.name}
          onChange={(event) => form.set('name', event.target.value)}
          {...error('name')}
        />
        <Input
          label={t('periods.field.position')}
          inputMode="numeric"
          required
          value={form.values.position}
          onChange={(event) => form.set('position', event.target.value)}
          {...error('position')}
        />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <Input
          label={t('periods.field.startsOn')}
          type="date"
          required
          value={form.values.startsOn}
          onChange={(event) => form.set('startsOn', event.target.value)}
          {...error('startsOn')}
        />
        <Input
          label={t('periods.field.endsOn')}
          type="date"
          required
          value={form.values.endsOn}
          onChange={(event) => form.set('endsOn', event.target.value)}
          {...error('endsOn')}
        />
      </div>
    </FormShell>
  );
};
